/** @jsxImportSource react */
import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { setCsrfToken, setProfile } from './authSlice'
import wretch from 'wretch'
import { createWretchInstance } from '../../utils/fetch'

import { getCsrfToken } from '../../utils/csrf'

const SigninForm = () => {
  const [email, setEmail] = useState('')
  const [otp, setOtp] = useState(['', '', '', '', '', ''])
  const [showOtpSection, setShowOtpSection] = useState(false)
  const [message, setMessage] = useState({ text: '', type: '' })
  const dispatch = useDispatch()

  const resetOtp = () => setOtp(['', '', '', '', '', ''])

  const handleOtpChange = (index, value) => {
    const newOtp = [...otp]
    newOtp[index] = value
    setOtp(newOtp)
    // Si un chiffre est saisi, passer au champ suivant
    if (value && index < otp.length - 1) {
      document.getElementById(`otp-input-${index + 1}`).focus()
    }
  }

  const handleOtpKeyDown = (e, index) => {
    if (e.key === 'Tab' && index < otp.length - 1) {
      e.preventDefault()
      document.getElementById(`otp-input-${index + 1}`).focus()
    }
  }
  const handleOtpKeyUp = (e, index) => {
    if (e.key === "Backspace" && index > 0) {
      e.preventDefault()
      document.getElementById(`otp-input-${index - 1}`).focus();
    }
  }

  const handleOtpPaste = (e) => {
    const data = e.clipboardData.getData('text')
    const value = data.split('')
    if (value.length === otp.length) {
      e.preventDefault()
      setOtp(value)
    }
  }

  const handleOtpBtnClick = async () => {
    if (!email) {
      setMessage({ text: 'Veuillez saisir votre adresse email', type: 'error' })
      return
    }

    createWretchInstance({ 'X-CSRF-Token': getCsrfToken() })
      .url('/auth/request_auth')
      .post({ email, auth_method: 'otp' })
      .notFound((error) => {        
        setMessage({text: error.json.errors.join(' '), type: "error"})
        setShowOtpSection(false)
      })
      .timeout( error => 
        setMessage({text: error.text || "Le serveur ne répond pas. Veuillez vérifier votre connexion internet ou réessayer plus tard.", type: "error"})
      )
      .fetchError( (error) => {
        setMessage({ text: error.text || "Quelque chose d'inattendu s'est produit... nous regardons ça de près au plus vite. Veuillez réessayer plus tard.", type: 'error' })
      })
      .json()
      .then( (json) => {
        setShowOtpSection(json.success)
        setMessage({ text: json.message, type: 'success' })
      })
  }

  const handleMagicLinkBtnClick = async () => {
    if (!email) {
      setMessage({ text: 'Veuillez saisir votre adresse email', type: 'error' })
      return
    }

    wretch('/auth/request_auth')
      .headers({ 'X-CSRF-Token': getCsrfToken(), 'Accept': 'application/json' })
      .post({ email })
      .notFound((error) => {        
        setMessage({text: error.json.errors.join(' '), type: "error"})
        setShowOtpSection(false)
      })
      .timeout( error => 
        setMessage({text: error.text || "Le serveur ne répond pas. Veuillez vérifier votre connexion internet ou réessayer plus tard.", type: "error"})
      )
      .fetchError( (error) => {
        setMessage({ text: error.text || "Quelque chose d'inattendu s'est produit... nous regardons ça de près au plus vite. Veuillez réessayer plus tard.", type: 'error' })
      })
      .json( (json) => {
        if(json.success) { window.location.href = json.redirect_url }
      })
  }

  const handleVerifyOtpBtnClick = async () => {
    const otpCode = otp.join('')

    wretch('/auth/verify_otp')
      .headers({ 'X-CSRF-Token': getCsrfToken() })
      .post({ email, code: otpCode })
      .unauthorized( (error) => {        
        setMessage({text: error.json.errors.join(' '), type: "error"})
        resetOtp()
        setShowOtpSection(false)
      })
      .timeout( error => 
        setMessage({text: error.text || "Le serveur ne répond pas. Veuillez vérifier votre connexion internet ou réessayer plus tard.", type: "error"})
      )
      .fetchError( (error) => {
        setMessage({ text: error.text || "Quelque chose d'innatendu s'est produit... nous regardons ça de près au plus vite. Veuillez réessayer plus tard.", type: 'error' })
      })
      .json( res => {
        dispatch(setCsrfToken(res.csrf))
        dispatch(setProfile(res.data))
        setMessage({text: res.message, type: 'success'})
        if(res.data.extranet_access) {
          window.location.href = '/extranet'
        } else {
          window.location.href = '/'
        }
      })

  }

  return (
    <div className="flex flex-col items-center">
      <h1 className="text-3xl font-bold text-primary mb-6">Connexion</h1>
      
      {message.text && (
        <div id="message" className={`alert ${message.type === 'error' ? 'alert-error' : 'alert-success'} mb-6 w-full`}>
          {message.text}
        </div>
      )}

      <div className="w-full mb-6">
        <label htmlFor="email" className="block text-gray-700 text-lg font-semibold mb-2">Adresse email :</label>
        <div className="relative">
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="input input-bordered w-full h-14 pr-12"
            required
          />
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <div className="bg-primary p-2 rounded-md">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {showOtpSection && (
        <div id="otp-section" className="w-full mb-6">
          <div className="mb-4">
            <label htmlFor="otp" className="block text-gray-700 text-lg font-semibold mb-2">Code reçu par mail :</label>
            <div className="flex space-x-2">
              {otp.map((value, index) => (
                <input
                  key={index}
                  type="text"
                  id={`otp-input-${index}`}
                  maxLength="1"
                  value={value}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(e, index)}
                  onKeyUp={(e) => handleOtpKeyUp(e, index)}
                  onFocus={(e) => e.target.select()}
                  onPaste={handleOtpPaste}
                  className="input input-bordered w-12 h-12 text-center"
                  required
                />
              ))}
            </div>
          </div>
          <button
            className="btn btn-primary w-full h-14 text-lg font-semibold"
            onClick={handleVerifyOtpBtnClick}
          >
            Vérifier
          </button>
        </div>
      )}

      {!showOtpSection && (
        <div id="auth-buttons" className="w-full grid grid-cols-2 gap-4">
          <button
            className="btn btn-primary h-20 text-white border-none text-base font-semibold"
            onClick={handleOtpBtnClick}
          >
            Recevoir un code
          </button>
          <button
            className="btn btn-primary h-20 text-white border-none text-base font-semibold"
            onClick={handleMagicLinkBtnClick}
          >
            Recevoir le lien<br/>de connexion<br/>par email
          </button>
        </div>
      )}
    </div>
  )
}

export default SigninForm